/**
 * PATH: apps/quote/src/components/builder/panels/context-panel/ContextPanel.tsx
 * PURPOSE: Orchestrator for context panel (Files, Emails, Drawings tabs)
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, Mail, Image } from 'lucide-react'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'
import TabButton, { type ContextTab } from './TabButton'
import FilesTab from './FilesTab'
import EmailsTab from './EmailsTab'
import DrawingsTab from './DrawingsTab'

interface ContextPanelProps {
  quoteId: string
  quote: QuoteDetail | null
}

export default function ContextPanel({ quoteId, quote }: ContextPanelProps) {
  const [activeTab, setActiveTab] = useState<ContextTab>('files')
  const isEditable = quote?.is_editable ?? true

  const { data: files = [] } = useQuery({
    queryKey: ['quoteAttachments', quoteId, 'file'],
    queryFn: () => quoteApi.listQuoteAttachments(quoteId, 'file'),
    staleTime: 30 * 1000,
  })

  const { data: emails = [] } = useQuery({
    queryKey: ['quoteAttachments', quoteId, 'email'],
    queryFn: () => quoteApi.listQuoteAttachments(quoteId, 'email'),
    staleTime: 30 * 1000,
  })

  const { data: contextData } = useQuery({
    queryKey: ['quoteContext', quoteId],
    queryFn: () => quoteApi.getQuoteContext(quoteId),
    staleTime: 30 * 1000,
  })

  return (
    <div className="h-full flex flex-col">
      {/* Sub-tabs */}
      <div className="flex-shrink-0 flex gap-1 p-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
        <TabButton tab="files" currentTab={activeTab} label="Files" icon={FileText} count={files.length} onClick={() => setActiveTab('files')} />
        <TabButton tab="emails" currentTab={activeTab} label="Emails" icon={Mail} count={emails.length} onClick={() => setActiveTab('emails')} />
        <TabButton tab="drawings" currentTab={activeTab} label="Drawings" icon={Image} count={contextData?.drawings?.length} onClick={() => setActiveTab('drawings')} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'files' && <FilesTab quoteId={quoteId} isEditable={isEditable} />}
        {activeTab === 'emails' && <EmailsTab quoteId={quoteId} isEditable={isEditable} />}
        {activeTab === 'drawings' && <DrawingsTab quoteId={quoteId} />}
      </div>
    </div>
  )
}
